import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../services/api";

import {
  Container,
  ContainerHeader,
  Content,
  Input,
  BtnSend,
  ErrorsArea,
  Logo,
  TitleSuccess,
  SubTitleSuccess,
  BackLink,
  Form,
} from "./styles";

const logoImg = require("../../assets/images/logo.png");

type Step = 'validating' | 'request' | 'reset' | 'invalid' | 'success';

// Nos erros o backend responde { error: "..." }; sem response é falha de rede.
function getErrorMessage(error: any, fallback: string): string {
  const message = error?.response?.data?.error;
  if (typeof message === "string" && message) return message;
  if (!error?.response) return "Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.";
  return fallback;
}

export default function PasswordReset() {
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [step, setStep] = useState<Step>(token ? 'validating' : 'request');
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [tokenError, setTokenError] = useState("");

  useEffect(() => {
    if (!token) return;
    let active = true;

    api.get(`/password/validate-token/${encodeURIComponent(token)}`)
      .then(() => {
        if (active) setStep('reset');
      })
      .catch((error: any) => {
        if (!active) return;
        const status = error.response?.status;
        if (status >= 400 && status < 500) {
          setTokenError(getErrorMessage(error, "Link inválido ou expirado"));
          setStep('invalid');
        } else {
          // Não deu para validar (rede/servidor): segue; o POST /password/reset valida o token de novo.
          setStep('reset');
        }
      });

    return () => {
      active = false;
    };
  }, [token]);

  const goToRequest = () => {
    setErrorMessages([]);
    setStep('request');
    setSearchParams({}, { replace: true });
  };

  const handleRequestReset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!email.trim()) {
      setErrorMessages(["Por favor, insira seu email"]);
      return;
    }

    setLoading(true);
    setErrorMessages([]);

    try {
      await api.post('/password/request-reset', { email: email.trim() });
      setSuccessMessage("Email de recuperação enviado! Verifique sua caixa de entrada.");
      setStep('success');
    } catch (error: any) {
      setErrorMessages([getErrorMessage(error, "Erro ao enviar email de recuperação")]);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      setErrorMessages(["Por favor, preencha todos os campos"]);
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessages(["As senhas não coincidem"]);
      return;
    }

    if (password.length < 6) {
      setErrorMessages(["A senha deve ter pelo menos 6 caracteres"]);
      return;
    }

    setLoading(true);
    setErrorMessages([]);

    try {
      await api.post('/password/reset', { 
        token, 
        newPassword: password 
      });
      setSuccessMessage("Senha redefinida com sucesso!");
      setStep('success');
    } catch (error: any) {
      setErrorMessages([getErrorMessage(error, "Erro ao redefinir senha")]);
    } finally {
      setLoading(false);
    }
  };

  const renderRequestForm = () => (
    <>
      <TitleSuccess>Recuperar Senha</TitleSuccess>
      <SubTitleSuccess>
        Digite seu email para receber as instruções de recuperação
      </SubTitleSuccess>
      
      <Form noValidate onSubmit={handleRequestReset}>
        <Input
          type="email"
          placeholder="Seu email"
          value={email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
        />

        <BtnSend type="submit" disabled={loading}>
          {loading ? "Enviando..." : "Enviar Email"}
        </BtnSend>
      </Form>
    </>
  );

  const renderResetForm = () => (
    <>
      <TitleSuccess>Nova Senha</TitleSuccess>
      <SubTitleSuccess>
        Digite sua nova senha
      </SubTitleSuccess>
      
      <Form noValidate onSubmit={handleResetPassword}>
        <Input
          type="password"
          placeholder="Nova senha"
          value={password}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
        />

        <Input
          type="password"
          placeholder="Confirme a nova senha"
          value={confirmPassword}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
        />

        <BtnSend type="submit" disabled={loading}>
          {loading ? "Redefinindo..." : "Redefinir Senha"}
        </BtnSend>
      </Form>

      <BackLink type="button" onClick={goToRequest}>
        Pedir novo link
      </BackLink>
    </>
  );

  const renderSuccess = () => (
    <>
      <TitleSuccess>Sucesso!</TitleSuccess>
      <SubTitleSuccess>{successMessage}</SubTitleSuccess>
    </>
  );

  const renderValidating = () => (
    <>
      <TitleSuccess>Nova Senha</TitleSuccess>
      <SubTitleSuccess>Validando link...</SubTitleSuccess>
    </>
  );

  const renderInvalidToken = () => (
    <>
      <TitleSuccess>Link inválido</TitleSuccess>
      <SubTitleSuccess>{tokenError}. Peça um novo link para redefinir sua senha.</SubTitleSuccess>
      <BtnSend type="button" onClick={goToRequest}>
        Pedir novo link
      </BtnSend>
    </>
  );

  return (
    <Container>
      <ContainerHeader>
        <Logo src={logoImg} alt="H7 Training" />
      </ContainerHeader>

      <Content>
        {step === 'validating' && renderValidating()}
        {step === 'request' && renderRequestForm()}
        {step === 'reset' && renderResetForm()}
        {step === 'invalid' && renderInvalidToken()}
        {step === 'success' && renderSuccess()}
        
        <ErrorsArea>
          {errorMessages.map((error, index) => (
            <p key={index}>{error}</p>
          ))}
        </ErrorsArea>
      </Content>
    </Container>
  );
}